import { useEffect } from 'react';

import ModalBasicProps from './type';
import Button from '../Button';
import Icon from '../Icon';
import IconButton from '../IconButton';
import Typography from '../Typography';
import { TucarTheme } from '../theme';
import {
  ItemModalStyled,
  BasicModalStyled,
  CenterModalBody,
  ExitButtonStyled,
  ConfirmButtonWrapperStyled,
} from './styled';

const Modal = ({ children, onClose, onConfirm, open, ...props }: ModalBasicProps) => {
  const { hideBackdrop, title, type, variant, ubication, style, confirmationMode, ...rest } = props;

  const iconAlert = () => {
    switch (type) {
      case 'success':
        return { name: 'checkCircleRounded', color: TucarTheme.colors.green600 };
      case 'informative':
        return { name: 'addAlertFilled', color: TucarTheme.colors.blue500 };
      case 'error':
        return { name: 'errorRounded', color: TucarTheme.colors.error500 };
      default:
        return { name: 'warningRounded', color: TucarTheme.colors.warning600 };
    }
  };

  const titleVariant = () => {
    const { color } =
      variant !== 'informative' ? iconAlert() : { color: TucarTheme.colors.blue500 };
    switch (variant) {
      case 'alert':
        return (
          <Typography variant="p" color={color} weight="medium">
            {title}
          </Typography>
        );
      case 'informative':
        return (
          <Typography variant="p" color={color} weight="medium">
            {title}
          </Typography>
        );
      case 'notification':
        return (
          <Typography
            style={{ fontSize: '20px', lineHeight: 1.2, textAlign: 'left', width: '100%' }}
            variant="h3"
            color={color}
            weight="medium"
          >
            {title}
          </Typography>
        );
      default:
        return '';
    }
  };

  useEffect(() => {
    const body = document.querySelector('body');
    if (open) {
      body.style.overflow = 'hidden';
      body.style.pointerEvents = 'none';
    } else {
      body.style.overflow = 'auto';
      body.style.pointerEvents = 'auto';
    }
  }, [open]);

  if (variant === 'custom') {
    return (
      <BasicModalStyled
        hideBackdrop={hideBackdrop}
        style={style}
        ubication={ubication}
        open={open}
        {...rest}
      >
        {children}
      </BasicModalStyled>
    );
  }

  if (variant === 'informative') {
    return (
      <BasicModalStyled
        hideBackdrop={hideBackdrop}
        variant={variant}
        style={style}
        ubication={ubication}
        open={open}
        {...rest}
      >
        <CenterModalBody>
          {title && titleVariant()}
          {children}
          {onConfirm && <Button onClick={onConfirm}>Aceptar</Button>}
        </CenterModalBody>
      </BasicModalStyled>
    );
  }

  if (variant === 'alert' && confirmationMode) {
    return (
      <BasicModalStyled
        hideBackdrop={hideBackdrop}
        variant={variant}
        style={style}
        ubication={ubication}
        open={open}
        {...rest}
      >
        <CenterModalBody>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ItemModalStyled type={type}>
              <Icon {...iconAlert()} />
            </ItemModalStyled>
            {title && titleVariant()}
          </div>
          {children}
          <ConfirmButtonWrapperStyled
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={onConfirm}>Aceptar</Button>
          </ConfirmButtonWrapperStyled>
        </CenterModalBody>
      </BasicModalStyled>
    );
  }

  if (variant === 'alert' && !confirmationMode) {
    return (
      <BasicModalStyled
        hideBackdrop={hideBackdrop}
        variant={variant}
        style={style}
        ubication={ubication}
        open={open}
        {...rest}
      >
        <ExitButtonStyled variant={variant}>
          <ItemModalStyled type={type}>
            <Icon {...iconAlert()} />
          </ItemModalStyled>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
              textAlign: 'start',
            }}
          >
            {title && titleVariant()}
            {children}
          </div>

          <IconButton
            data-testid="clearButtonModal"
            onClick={onClose}
            variant="ghost"
            iconProps={{ name: 'clearRounded', color: '#17171C', size: '24px' }}
          />
        </ExitButtonStyled>
      </BasicModalStyled>
    );
  }

  return (
    <BasicModalStyled
      hideBackdrop={hideBackdrop || true}
      variant={variant}
      style={style}
      ubication={ubication}
      open={open}
      {...rest}
    >
      <ExitButtonStyled variant={variant}>
        <ItemModalStyled type={type}>
          <Icon {...iconAlert()} />
        </ItemModalStyled>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'start',
            width: '100%',
            height: '100%',
            justifyContent: children ? 'start' : 'center',
          }}
        >
          {title && titleVariant()}
          {children}
        </div>

        <IconButton
          data-testid="clearButtonModal"
          onClick={onClose}
          variant="ghost"
          iconProps={{ name: 'clearRounded', color: '#17171C', size: '24px' }}
        />
      </ExitButtonStyled>
    </BasicModalStyled>
  );
};

Modal.defaultProps = {
  variant: 'custom',
  type: 'informative',
  title: '',
  ubication: 'center',
  children: null,
};

export default Modal;
